// FR-M4 / F17 §4: the shared "tell the call it is being recorded" step. Reused by the
// extension (any-call and Meet), the live-meeting preview, and — later — F8 (desktop) and
// F9 (bot). It replaces the old ConsentPrompt rather than sitting beside it.
//
// All-party is the default; an org may relax to one-party, never the reverse. When the mode
// requires an announcement, Record stays blocked until the user confirms "I've announced it";
// otherwise the same text is informational.

import { useState } from "react";
import type { MeetingConsentMode } from "../api/types";
import { Button, Callout, Card } from "@grackle/ui";
import { announceConsent } from "./client";

export interface ConsentAnnouncementProps {
  consentMode: MeetingConsentMode;
  orgName?: string;
  /** Block Record until "I've announced it". Defaults to the mode: all-party requires it. */
  required?: boolean;
  /**
   * The live session to stamp. Without one (the session does not exist until capture
   * starts), `onConfirm` receives the confirmation time and the host posts it later.
   */
  sessionId?: string;
  /** POSTs `/live-transcripts/{id}/consent-announced`; injectable for hosts without the web api client. */
  announce?: (sessionId: string) => Promise<unknown>;
  onConfirm: (announcedAt: string | null) => void;
  onCancel?: () => void;
  busy?: boolean;
  recordLabel?: string;
  /** Meet only, and only offered when the host granted site permission: best-effort chat post. */
  onPostToChat?: (text: string) => Promise<boolean>;
}

export function announcementText(orgName: string | undefined): string {
  const who = orgName?.trim() || "your organization";
  return `This call is being recorded and transcribed by Grackle for ${who}. Say so now if you object.`;
}

export function announcementRequired(mode: MeetingConsentMode): boolean {
  return mode === "all_party";
}

export function consentCopy(mode: MeetingConsentMode): { title: string; body: string; confirm: string } {
  if (mode === "one_party") {
    return {
      title: "Confirm you are recording",
      body: "This organization allows one-party consent. You are still responsible for telling people they are being recorded where the law or the meeting requires it.",
      confirm: "I've announced it",
    };
  }
  return {
    title: "Everyone must be told",
    // FR-M4: all-party is the default and the strictest mode.
    body: "This organization requires all-party consent: everyone in the meeting must know it's being recorded. A visible recording banner will appear in the meeting. Do not start until everyone present has been told.",
    confirm: "I've announced it",
  };
}

export default function ConsentAnnouncement({
  consentMode,
  orgName,
  required,
  sessionId,
  announce = announceConsent,
  onConfirm,
  onCancel,
  busy,
  recordLabel = "Start recording",
  onPostToChat,
}: ConsentAnnouncementProps) {
  const copy = consentCopy(consentMode);
  const text = announcementText(orgName);
  const mustAnnounce = required ?? announcementRequired(consentMode);
  const [announcedAt, setAnnouncedAt] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function copyText(): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  async function onCopy() {
    setNote((await copyText()) ? "Copied. Paste it into the call chat." : "Couldn't copy — select the text above.");
  }

  async function onChat() {
    if (!onPostToChat) return;
    let posted = false;
    try {
      posted = await onPostToChat(text);
    } catch {
      posted = false;
    }
    if (posted) {
      setNote("Posted to the Meet chat.");
      return;
    }
    // Best effort: the chat DOM moves around, so fall back to Copy.
    setNote((await copyText()) ? "Couldn't post to the Meet chat. Copied instead — paste it there." : "Couldn't post to the Meet chat.");
  }

  async function onAnnounced(checked: boolean) {
    if (!checked) {
      setAnnouncedAt(null);
      return;
    }
    setAnnouncedAt(new Date().toISOString());
    if (sessionId) {
      try {
        await announce(sessionId);
      } catch {
        setNote("Couldn't log the announcement. Recording can still start.");
      }
    }
  }

  const blocked = mustAnnounce && announcedAt === null;
  return (
    <Card size="sm" className="consent-prompt" role="alertdialog" aria-labelledby="consent-title" aria-describedby="consent-body">
      <Callout tone="record" icon="record">
        Grackle will announce this recording — everyone in the meeting can see the banner
      </Callout>
      <h2 id="consent-title">{copy.title}</h2>
      <p id="consent-body">{copy.body}</p>
      <blockquote className="consent-text" data-testid="consent-text">
        {text}
      </blockquote>
      <div className="consent-actions">
        <Button variant="secondary" size="sm" icon="copy" onClick={() => void onCopy()}>
          Copy
        </Button>
        {onPostToChat ? (
          <Button variant="secondary" size="sm" onClick={() => void onChat()}>
            Post to Meet chat
          </Button>
        ) : null}
      </div>
      {note ? (
        <p className="consent-note" role="status">
          {note}
        </p>
      ) : null}
      <label className="consent-confirm">
        <input type="checkbox" checked={announcedAt !== null} onChange={(e) => void onAnnounced(e.target.checked)} />
        <span>{copy.confirm}</span>
      </label>
      <div className="consent-actions">
        {onCancel ? (
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        ) : null}
        <Button variant="primary" disabled={busy || blocked} onClick={() => onConfirm(announcedAt)}>
          {recordLabel}
        </Button>
      </div>
    </Card>
  );
}
